import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Eye, FileQuestion, Pencil, Play, RotateCcw, ShieldCheck, X } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { AppButton, AppCard, AppCardContent, Callout, EmptyState, PageHeader } from "../ui";
import Shimmer from "../Common/Shimmer";
// The two pieces directly, not the ExamRunner index: that also loads the public
// exam's own HTTP client, which a staff page has no use for.
import ExamRunner from "../ExamRunner/ExamRunner";
import createPreviewAdapter from "../ExamRunner/adapters/previewAdapter";
import AssessmentBriefing from "../StudentAssessments/AssessmentBriefing";
import { formatDuration, guardsOf, plural } from "../StudentAssessments/studentAssessmentRules";
import { assessmentPreviewTrail, assessmentsBase, isLibraryPath, parentOf } from "../QuizBank/quizCenterTrail";

/** The strip over the exam screen while a preview runs. */
function PreviewBar({ onExit }) {
  return (
    <div className="tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1.5 tw-border-0 tw-border-b tw-border-solid tw-border-line tw-bg-primary-soft tw-px-4 tw-py-2 sm:tw-px-6">
      <span className="tw-inline-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2 tw-text-[0.8125rem] tw-text-content">
        <Eye size={15} aria-hidden="true" className="tw-shrink-0 tw-text-primary" />
        <span>
          <strong>Preview</strong> — this is the exam as a student sits it. Nothing is saved, and the security rules
          are not enforced.
        </span>
      </span>
      <AppButton type="button" size="xs" variant="outline" onClick={onExit}>
        <X size={14} aria-hidden="true" /> Exit preview
      </AppButton>
    </div>
  );
}

/**
 * AssessmentPreviewPage — `/assessments/:id/preview`, and the platform
 * library's `/sa/marketplace/assessments/:id/preview`: an assessment the way a
 * student meets it, for the person who made it.
 *
 * First the briefing the student reads before starting (the student page's own
 * card), then the real exam screen — the same ExamRunner, on a preview adapter
 * that stores nothing — then a short "that's the end" with a way to go again.
 * The paper is built by the server exactly as a start builds it, so random
 * draws, shuffling and timed sections are what a student would get; "Start
 * again" fetches a fresh draw.
 */
export default function AssessmentPreviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isLibrary = isLibraryPath(useLocation().pathname);
  const editPath = `${assessmentsBase(isLibrary)}/edit/${id}`;

  const [load, setLoad] = useState({ state: "loading", message: "" });
  const [payload, setPayload] = useState(null);
  const [stage, setStage] = useState("briefing"); // briefing | sitting | done
  const [run, setRun] = useState(0);
  const [result, setResult] = useState(null);

  const fetchPreview = useCallback(async () => {
    setLoad({ state: "loading", message: "" });
    try {
      const { data } = await apiClient.get(`/assessments/${id}/preview`);
      setPayload(data);
      setLoad({ state: "ready", message: "" });
    } catch (err) {
      const status = err?.response?.status;
      const body = err?.response?.data || {};
      setLoad({
        state: status === 404 ? "missing" : body.errorCode === "EMPTY_PAPER" ? "empty" : "error",
        message: body.message || "",
      });
    }
  }, [id]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  // A fresh adapter per run: every start begins with no answers.
  const adapter = useMemo(
    () => (payload ? createPreviewAdapter({ payload, base: `/assessments/${id}` }) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payload, id, run],
  );

  const start = () => { setResult(null); setRun((n) => n + 1); setStage("sitting"); };
  const again = async () => { setStage("briefing"); setResult(null); await fetchPreview(); };

  const trail = assessmentPreviewTrail({ isLibrary, id, title: payload?.assessment?.title });
  const backPath = parentOf(trail);

  const header = (
    <PageHeader
      breadcrumbs={trail}
      icon={Eye}
      title="Preview as a student"
      description={payload?.assessment?.title || "How students see this assessment"}
      actions={(
        <AppButton variant="outline" onClick={() => navigate(editPath)}>
          <Pencil size={16} aria-hidden="true" /> Edit
        </AppButton>
      )}
    />
  );

  if (load.state === "loading") {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4" aria-busy="true" aria-label="Loading the preview">
        {header}
        <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-3xl tw-flex-col tw-gap-4">
          <Shimmer height={64} />
          <Shimmer height={360} />
        </div>
      </div>
    );
  }

  if (load.state !== "ready") {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {header}
        {load.state === "missing" && (
          <EmptyState icon={FileQuestion} title="Assessment not found" description="It may have been deleted." />
        )}
        {load.state === "empty" && (
          <EmptyState
            icon={FileQuestion}
            title="Nothing to preview yet"
            description={load.message || "Add questions to this assessment, then preview it."}
            action={<AppButton onClick={() => navigate(editPath)}><Pencil size={16} aria-hidden="true" /> Add questions</AppButton>}
          />
        )}
        {load.state === "error" && (
          <Callout tone="danger" role="alert" action={<AppButton size="xs" variant="outline" onClick={fetchPreview}>Try again</AppButton>}>
            {load.message || "The preview could not be built."}
          </Callout>
        )}
      </div>
    );
  }

  const a = payload.assessment || {};
  const guards = guardsOf(a.antiCheat);

  if (stage === "sitting" && adapter) {
    return (
      <ExamRunner
        key={run}
        adapter={adapter}
        banner={<PreviewBar onExit={() => setStage("briefing")} />}
        statusText="Preview — nothing is saved"
        onFinished={(r) => { setResult(r); setStage("done"); }}
      />
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {header}
      <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-3xl tw-flex-col tw-gap-4">
        {stage === "done" ? (
          <FinishedCard result={result} onAgain={again} onEdit={() => navigate(editPath)} onBack={() => navigate(backPath)} isLibrary={isLibrary} />
        ) : (
          <>
            <Callout tone="info">
              This is what a student reads before starting — then the exam itself, exactly as they sit it. Nothing you
              do here is saved, counted as an attempt or sent to anyone.
              {payload.preview?.drawsPerStudent && " Every student gets their own draw of questions; this is one of them — Start again for another."}
            </Callout>
            {a.status === "draft" && (
              <Callout tone="warning">This is a draft. Students cannot see it until it is published.</Callout>
            )}

            <AssessmentBriefing
              assessment={a}
              action={(
                <AppButton onClick={start}>
                  <Play size={16} aria-hidden="true" /> Start the preview
                </AppButton>
              )}
            />

            {guards.length > 0 && (
              <AppCard as="section" aria-labelledby="preview-guards">
                <AppCardContent className="tw-flex tw-flex-col tw-gap-2">
                  <h2 id="preview-guards" className="tw-m-0 tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-content">
                    <ShieldCheck size={16} aria-hidden="true" className="tw-text-primary" /> What students also get
                  </h2>
                  <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">
                    The preview does not enforce these, so you can look around freely.
                  </p>
                  <ul className="tw-m-0 tw-flex tw-list-disc tw-flex-col tw-gap-1 tw-pl-5 tw-text-[0.8125rem] tw-text-content">
                    {guards.map((g) => <li key={g}>{g}</li>)}
                  </ul>
                </AppCardContent>
              </AppCard>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** "That is the end of the preview" — how far it got, and where to go next. */
function FinishedCard({ result, onAgain, onEdit, onBack, isLibrary }) {
  const timedOut = result?.reason === "time_expired";
  return (
    <AppCard as="section" aria-labelledby="preview-done">
      <AppCardContent className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-8 tw-text-center">
        <CheckCircle2 size={36} aria-hidden="true" className="tw-text-success" />
        <h2 id="preview-done" className="tw-m-0 tw-text-lg tw-font-semibold tw-text-content">That is the end of the preview</h2>
        <p className="tw-m-0 tw-max-w-lg tw-text-sm tw-text-content-muted">
          {timedOut
            ? "Time ran out — at this point a student's exam is submitted for them, with whatever they had answered."
            : "A student's exam is submitted at this point, and they are taken to their result."}
        </p>
        {result?.total > 0 && (
          <p className="tw-m-0 tw-text-sm tw-text-content">
            You answered {result.answered} of {plural(result.total, "question")}
            {result.timeSpent > 0 ? ` in ${formatDuration(result.timeSpent)}` : ""}.
          </p>
        )}
        <p className="tw-m-0 tw-text-xs tw-text-content-subtle">Nothing was saved, no attempt was counted and no one was told.</p>
        <div className="tw-mt-2 tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
          <AppButton onClick={onAgain}>
            <RotateCcw size={16} aria-hidden="true" /> Start again
          </AppButton>
          <AppButton variant="outline" onClick={onEdit}>
            <Pencil size={16} aria-hidden="true" /> Edit the assessment
          </AppButton>
          <AppButton variant="outline" onClick={onBack}>
            {isLibrary ? "Back to the library" : "Back to the assessment"}
          </AppButton>
        </div>
      </AppCardContent>
    </AppCard>
  );
}
